import { useEffect, useMemo } from 'react'
import { ArcElement, Chart as ChartJS, Legend, Tooltip } from 'chart.js'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { Doughnut } from 'react-chartjs-2'

import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks'
import {
  resetExamStatsFilters,
  setExamStatsFilters,
} from '@state/reducers/statsReducer'
import { GetExamStatsQuery } from '@services/examStatsService'
import { getExamStats, getExamStatsOptions } from '@state/thunks/statsThunk'

ChartJS.register(ArcElement, Tooltip, Legend)

const chartColors = [
  '#9d03fc',
  '#18ab32',
  '#5733FF',
  '#5d63fc',
  '#33eee3',
  '#Ee555F',
  '#FF6384',
  '#36A2EB',
  '#FFCE56',
  '#4BC0C0',
]

export default function ExamStats() {
  const dispatch = useAppDispatch()
  const {
    filters,
    sites,
    exams,
    total,
    breakdown,
    loading,
    loadingOptions,
    error,
    hasLoaded,
    hasLoadedOptions,
  } = useAppSelector(({ stats }) => stats.examStats)

  useEffect(() => {
    if (!hasLoadedOptions) {
      void dispatch(getExamStatsOptions())
    }

    if (!hasLoaded) {
      void dispatch(getExamStats())
    }
  }, [dispatch, hasLoaded, hasLoadedOptions])

  const sortedBreakdown = useMemo(
    () => [...breakdown].sort((left, right) => right.total - left.total),
    [breakdown],
  )

  const examTypeChartData = useMemo(() => {
    const totalsByType = breakdown.reduce<Record<string, number>>((accumulator, item) => {
      const key = item.examTypeLabel || 'Non renseigné'
      accumulator[key] = (accumulator[key] ?? 0) + item.total
      return accumulator
    }, {})

    const entries = (Object.entries(totalsByType) as [string, number][]).sort(
      (left, right) => right[1] - left[1],
    )

    return {
      labels: entries.map(([label]) => label),
      datasets: [
        {
          data: entries.map(([, value]) => value),
          backgroundColor: entries.map((_, index) => chartColors[index % chartColors.length]),
          borderWidth: 2,
          hoverOffset: 6,
        },
      ],
    }
  }, [breakdown])

  const handleFilterChange = (
    key: keyof GetExamStatsQuery,
    value: string | number | undefined,
  ) => {
    dispatch(
      setExamStatsFilters({
        [key]: value,
      }),
    )
  }

  const handleSiteChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value
    handleFilterChange('siteId', value === '' ? undefined : Number(value))
  }

  const handleExamChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value
    handleFilterChange('examId', value === '' ? undefined : Number(value))
  }

  const handleApplyFilters = () => {
    void dispatch(getExamStats())
  }

  const handleResetFilters = () => {
    dispatch(resetExamStatsFilters())
    void dispatch(getExamStats())
  }

  return (
    <Box sx={{ padding: 3, paddingRight: 2 }}>
      <Grid container spacing={2}>
        <Grid
          item
          xs={12}
          sx={{
            position: 'sticky',
            top: 16,
            zIndex: 10,
          }}
        >
          <Card sx={{ borderRadius: 3 }}>
            <CardContent>
              <Typography variant="h5" sx={{ marginBottom: 2 }}>
                Statistiques des examens
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <InputLabel id="exam-stats-site-label">Site</InputLabel>
                    <Select
                      labelId="exam-stats-site-label"
                      value={filters.siteId?.toString() ?? ''}
                      label="Site"
                      onChange={handleSiteChange}
                      disabled={loadingOptions}
                    >
                      <MenuItem value="">Tous les sites</MenuItem>
                      {sites.map((site) => (
                        <MenuItem key={site.id} value={site.id.toString()}>
                          {site.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <InputLabel id="exam-stats-exam-label">Examen</InputLabel>
                    <Select
                      labelId="exam-stats-exam-label"
                      value={filters.examId?.toString() ?? ''}
                      label="Examen"
                      onChange={handleExamChange}
                      disabled={loadingOptions}
                    >
                      <MenuItem value="">Tous les examens</MenuItem>
                      {exams.map((exam) => (
                        <MenuItem key={exam.id} value={exam.id.toString()}>
                          {exam.label} ({exam.code})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Date de début"
                    value={filters.startDate}
                    onChange={(event) => handleFilterChange('startDate', event.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Date de fin"
                    value={filters.endDate}
                    onChange={(event) => handleFilterChange('endDate', event.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={2}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <Button variant="contained" onClick={handleApplyFilters} disabled={loading}>
                    Appliquer
                  </Button>
                  <Button variant="outlined" onClick={handleResetFilters} disabled={loading}>
                    Réinitialiser
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {error && (
          <Grid item xs={12}>
            <Alert severity="error">{error}</Alert>
          </Grid>
        )}

        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%', borderRadius: 3 }}>
            <CardContent>
              <Typography variant="overline" color="text.secondary">
                Total des examens
              </Typography>
              <Typography variant="h3">{total}</Typography>
              <Typography variant="body2" color="text.secondary">
                Nombre pour la période et les filtres sélectionnés.
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={8}>
          <Card sx={{ height: '100%', borderRadius: 3 }}>
            <CardContent>
              <Typography variant="overline" color="text.secondary">
                Lignes de détail
              </Typography>
              <Typography variant="h3">{sortedBreakdown.length}</Typography>
              <Typography variant="body2" color="text.secondary">
                Combinaisons distinctes de type et code d&apos;examen renvoyées par l&apos;API.
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={7}>
          <Card sx={{ borderRadius: 3, height: '100%' }}>
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: 2,
                }}
              >
                <Typography variant="h5">Répartition par type et code d&apos;examen</Typography>
                {(loading || loadingOptions) && <CircularProgress size={24} />}
              </Box>

              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Type d&apos;examen</TableCell>
                      <TableCell>Code examen</TableCell>
                      <TableCell>Libellé examen</TableCell>
                      <TableCell align="right">Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {!loading && sortedBreakdown.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          Aucune statistique d&apos;examen trouvée pour les filtres sélectionnés.
                        </TableCell>
                      </TableRow>
                    )}

                    {sortedBreakdown.map((item) => (
                      <TableRow key={`${item.examId}-${item.examCode}`}>
                        <TableCell>{item.examTypeLabel}</TableCell>
                        <TableCell>{item.examCode}</TableCell>
                        <TableCell>{item.examLabel}</TableCell>
                        <TableCell align="right">{item.total}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={5}>
          <Card sx={{ borderRadius: 3, height: '100%' }}>
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: 2,
                }}
              >
                <Typography variant="h5">Répartition par type d&apos;examen</Typography>
                {(loading || loadingOptions) && <CircularProgress size={24} />}
              </Box>

              {examTypeChartData.labels.length === 0 && !loading ? (
                <Box
                  sx={{
                    minHeight: 320,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography color="text.secondary">
                    Aucune donnée disponible pour afficher le graphique.
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ height: 360 }}>
                  <Doughnut
                    data={examTypeChartData}
                    options={{
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          position: 'right',
                        },
                      },
                    }}
                  />
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  )
}
